import { useState, useEffect, useCallback } from 'react';
import useIsMounted from '../../shared/hooks/useIsMounted';
import { fetchWithAuth } from '../auth/fetchWithAuth';
import { API_URL } from '../../config/env';

const BASE_PATH = `${API_URL}/v1/admin/tags`;

async function parseErrorBody(res) {
  const body = await res.json().catch(() => ({}));
  return body.errors?.join(', ') || body.error || res.statusText;
}

export default function useTags({ enabled = true } = {}) {
  const isMounted = useIsMounted();
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchTags = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchWithAuth(BASE_PATH);
      if (!res.ok) throw new Error(await parseErrorBody(res));
      const data = await res.json();
      setTags(Array.isArray(data) ? data : []);
    } catch (err) {
      if (!isMounted.current) return;
      console.error(err);
      setError(err.message || 'Failed to load tags');
      setTags([]);
    } finally {
      if (!isMounted.current) return;
      setLoading(false);
    }
  }, [enabled, isMounted]);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  const createTag = useCallback(async (name) => {
    setError(null);
    try {
      const res = await fetchWithAuth(BASE_PATH, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tag: { name } }),
      });
      if (!res.ok) throw new Error(await parseErrorBody(res));
      const tag = await res.json();
      await fetchTags();
      return tag;
    } catch (err) {
      if (!isMounted.current) return;
      console.error(err);
      setError(err.message || 'Failed to create tag');
      throw err;
    }
  }, [fetchTags, isMounted]);

  const renameTag = useCallback(async (tagId, name) => {
    setError(null);
    try {
      const res = await fetchWithAuth(`${BASE_PATH}/${tagId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tag: { name } }),
      });
      if (!res.ok) throw new Error(await parseErrorBody(res));
      await fetchTags();
    } catch (err) {
      if (!isMounted.current) return;
      console.error(err);
      setError(err.message || 'Failed to rename tag');
      throw err;
    }
  }, [fetchTags, isMounted]);

  const deleteTag = useCallback(async (tagId) => {
    setError(null);
    try {
      const res = await fetchWithAuth(`${BASE_PATH}/${tagId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(await parseErrorBody(res));
      await fetchTags();
    } catch (err) {
      if (!isMounted.current) return;
      console.error(err);
      setError(err.message || 'Failed to delete tag');
      throw err;
    }
  }, [fetchTags, isMounted]);

  const mergeTag = useCallback(async (fromTagId, targetTagId) => {
    setError(null);
    try {
      const res = await fetchWithAuth(`${BASE_PATH}/${fromTagId}/merge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ target_tag_id: targetTagId }),
      });
      if (!res.ok) throw new Error(await parseErrorBody(res));
      await fetchTags();
    } catch (err) {
      if (!isMounted.current) return;
      console.error(err);
      setError(err.message || 'Failed to merge tag');
      throw err;
    }
  }, [fetchTags, isMounted]);

  const addParent = useCallback(async (tagId, parentTagId) => {
    setError(null);
    try {
      const res = await fetchWithAuth(`${BASE_PATH}/${tagId}/tag_parents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ parent_tag_id: parentTagId }),
      });
      if (!res.ok) throw new Error(await parseErrorBody(res));
      await fetchTags();
    } catch (err) {
      if (!isMounted.current) return;
      console.error(err);
      setError(err.message || 'Failed to add parent tag');
      throw err;
    }
  }, [fetchTags, isMounted]);

  const removeParent = useCallback(async (tagId, parentTagId) => {
    setError(null);
    try {
      const res = await fetchWithAuth(`${BASE_PATH}/${tagId}/tag_parents/${parentTagId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(await parseErrorBody(res));
      await fetchTags();
    } catch (err) {
      if (!isMounted.current) return;
      console.error(err);
      setError(err.message || 'Failed to remove parent tag');
      throw err;
    }
  }, [fetchTags, isMounted]);

  return {
    tags,
    loading,
    error,
    refresh: fetchTags,
    createTag,
    renameTag,
    deleteTag,
    mergeTag,
    addParent,
    removeParent,
  };
}
